import { useEffect, useMemo, useRef, useState } from "react";
import Lottie from "lottie-react";
import {
  chestAnimationData,
  CHEST_ANIMATION_HOLD_FRAME,
  CHEST_GEM_REVEAL_FRAME,
} from "../constants/chestAnimation";
import { GAME_LEVELS, GAME_QUESTION_COUNT } from "../constants/gameLevels";
import GameProgressDelta from "./GameProgressDelta";
import "./GameProgressDelta.css";
import "./ChestOpenAnimation.css";

export default function ChestOpenAnimation({
  score,
  levelId,
  rewardGem,
  progressDelta = null,
  cloverPotionId = null,
  onContinue,
  continueLabel = "Continuer",
  previewAutoOpen = false,
}) {
  const lottieRef = useRef(null);
  const stoppedRef = useRef(false);
  const isOpeningRef = useRef(false);
  const gemRevealedRef = useRef(false);

  const [isOpening, setIsOpening] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [gemEmerging, setGemEmerging] = useState(false);

  const gem = useMemo(() => rewardGem || null, [rewardGem]);

  const levelName =
    GAME_LEVELS.find((level) => level.id === levelId)?.name || "inconnu";

  const handleLoaded = () => {
    stoppedRef.current = true;
    lottieRef.current?.goToAndStop(0, true);
  };

  const finishOpening = () => {
    stoppedRef.current = true;
    lottieRef.current?.goToAndStop(CHEST_ANIMATION_HOLD_FRAME, true);
    setIsOpen(true);
  };

  const handleEnterFrame = () => {
    const frame = lottieRef.current?.animationItem?.currentFrame ?? 0;

    if (
      isOpeningRef.current &&
      !gemRevealedRef.current &&
      frame >= CHEST_GEM_REVEAL_FRAME
    ) {
      gemRevealedRef.current = true;
      setGemEmerging(true);
    }

    if (stoppedRef.current) return;

    if (frame >= CHEST_ANIMATION_HOLD_FRAME) {
      finishOpening();
    }
  };

  const handleChestClick = () => {
    if (isOpening || isOpen) return;

    isOpeningRef.current = true;
    setIsOpening(true);
    stoppedRef.current = false;
    lottieRef.current?.playSegments([0, CHEST_ANIMATION_HOLD_FRAME], true);
  };

  const handleChestKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleChestClick();
    }
  };

  const canOpen = !isOpening && !isOpen;

  useEffect(() => {
    if (!previewAutoOpen || isOpen || isOpening) return;
    const timer = window.setTimeout(() => {
      if (isOpeningRef.current || stoppedRef.current) return;
      isOpeningRef.current = true;
      setIsOpening(true);
      stoppedRef.current = false;
      lottieRef.current?.playSegments([0, CHEST_ANIMATION_HOLD_FRAME], true);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [previewAutoOpen, isOpen, isOpening]);

  return (
    <div className={`chest-reward${isOpen ? " chest-reward--open" : ""}`}>
      <header className="chest-reward__header">
        <p className="chest-reward__score">
          Félicitation, vous avez obtenu un score de {score} /{" "}
          {GAME_QUESTION_COUNT} au niveau {levelName}.
        </p>
        {progressDelta ? (
          <GameProgressDelta
            discovery={progressDelta.discovery}
            mastery={progressDelta.mastery}
            score={score}
            totalQuestions={GAME_QUESTION_COUNT}
            layout="celebration"
            cloverPotionId={cloverPotionId}
            className="chest-reward__progress-delta"
          />
        ) : null}
      </header>

      <div className="chest-reward__stage">
        <div
          className={`chest-reward__chest${canOpen ? " chest-reward__chest--clickable" : ""}`}
          onClick={canOpen ? handleChestClick : undefined}
          onKeyDown={canOpen ? handleChestKeyDown : undefined}
          role={canOpen ? "button" : undefined}
          tabIndex={canOpen ? 0 : undefined}
          aria-label={
            canOpen
              ? "Coffre fermé, cliquez pour ouvrir"
              : "Coffre au trésor ouvert"
          }
        >
          <Lottie
            lottieRef={lottieRef}
            animationData={chestAnimationData}
            loop={false}
            autoplay={false}
            onDOMLoaded={handleLoaded}
            onEnterFrame={handleEnterFrame}
            onComplete={finishOpening}
            className="chest-reward__lottie"
          />

          {gemEmerging && gem && (
            <div className="chest-reward__gem-wrap chest-reward__gem-wrap--emerge">
              <div className="chest-reward__gem-glow" aria-hidden="true" />
              <img
                src={gem.imageUrl}
                alt={gem.label}
                className="chest-reward__gem"
                decoding="sync"
              />
            </div>
          )}
        </div>

        {canOpen && (
          <p className="chest-reward__hint">
            Cliquez sur le coffre pour découvrir votre récompense !
          </p>
        )}

        {isOpen && gem && (
          <p className="chest-reward__reward-msg">
            Vous obtenez 1{" "}
            <span className="chest-reward__reward-gem">{gem.label}</span> !
          </p>
        )}
      </div>

      {isOpen && onContinue && (
        <footer className="chest-reward__footer">
          <button
            type="button"
            className="chest-reward__continue"
            onClick={onContinue}
          >
            {continueLabel}
          </button>
        </footer>
      )}
    </div>
  );
}
